import { useCallback, useRef, useState } from "react";

const JOB_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours
const STORAGE_KEY_PREFIX = "Stitch-bulk-document-jobs-";

export interface BulkDocumentJob {
  jobId: string;
  filename: string;
  fileSize?: number;
  documentId: number;
  bulkUploadId: string;
  createdAt: number;
  failed?: boolean;
}

function storageKey(userId: string) {
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

function loadJobs(userId: string): BulkDocumentJob[] {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const parsed: BulkDocumentJob[] = JSON.parse(raw);
    const cutoff = Date.now() - JOB_MAX_AGE_MS;
    return parsed.filter((j) => j.createdAt > cutoff);
  } catch {
    return [];
  }
}

function saveJobs(userId: string, jobs: BulkDocumentJob[]) {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(jobs));
  } catch {
    // Storage quota or private mode — degrade gracefully.
  }
}

export function useBulkDocumentJobs(userId: string) {
  const [jobs, setJobs] = useState<BulkDocumentJob[]>(() => loadJobs(userId));
  const jobsRef = useRef(jobs);

  const addJobs = useCallback(
    (newJobs: BulkDocumentJob[]) => {
      const next = [...jobsRef.current, ...newJobs];
      jobsRef.current = next;
      saveJobs(userId, next);
      setJobs(next);
    },
    [userId],
  );

  const markJobFailed = useCallback(
    (jobId: string) => {
      const next = jobsRef.current.map((j) =>
        j.jobId === jobId ? { ...j, failed: true } : j,
      );
      jobsRef.current = next;
      saveJobs(
        userId,
        next.filter((j) => !j.failed),
      );
      setJobs(next);
    },
    [userId],
  );

  const removeJob = useCallback(
    (jobId: string) => {
      const next = jobsRef.current.filter((j) => j.jobId !== jobId);
      jobsRef.current = next;
      saveJobs(userId, next);
      setJobs(next);
    },
    [userId],
  );

  return { jobs, addJobs, markJobFailed, removeJob };
}
